import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import {
  Phone,
  Search,
  ArrowForward,
  PhoneCallback,
  PhoneMissed,
  Close,
  AccessTime,
  Person,
  Business,
  RecordVoiceOver,
  Assignment,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { useHistory } from "react-router-dom";
import { COLORS } from "./constants";

function avatarColor(name) {
  const LIST = ["#5c6bc0", "#26a69a", "#ef5350", "#ffa726", "#66bb6a", "#ab47bc"];
  if (!name) return LIST[0];
  let n = 0;
  for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
  return LIST[n % LIST.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function OutcomePill({ outcome }) {
  const lower = (outcome || "").toLowerCase();
  const map = {
    answered: { color: "success", label: "Answered" },
    missed: { color: "error", label: "Missed" },
    "no answer": { color: "warning", label: "No Answer" },
    busy: { color: "warning", label: "Busy" },
    voicemail: { color: "default", label: "Voicemail" },
    "call back": { color: "info", label: "Call Back" },
  };
  const cfg = map[lower] || { color: "default", label: outcome || "—" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      color={cfg.color}
      sx={{ borderRadius: "12px", fontWeight: 600, fontSize: "0.7rem", height: 22 }}
    />
  );
}

// ── Call Detail Drawer ────────────────────────────────────────────────────────
function DetailRow({ icon: Icon, label, value, color }) {
  if (!value) return null;
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={1}
      sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: "8px",
        bgcolor: (color || COLORS.indigo) + "18",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: 16, color: color || COLORS.indigo }} />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.82rem", wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function CallDetailDrawer({ call, open, onClose, users = [] }) {
  if (!call) return null;

  const contactName = call.contact_name || call.contact?.name || "Unknown";
  const companyName = call.company_name || call.contact?.company_name || call.contact?.company || "";
  const dt = call.start_time ? parseISO(call.start_time) : null;

  const getUserName = (idOrObj) => {
    if (!idOrObj) return null;
    if (typeof idOrObj === "object") {
      return [idOrObj.first_name, idOrObj.last_name].filter(Boolean).join(" ") || idOrObj.username;
    }
    const u = users.find((u) => String(u.id) === String(idOrObj) || String(u.auth_user_id) === String(idOrObj));
    return u ? [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username : null;
  };

  // Prefer the names the API resolves: the users roster only lists active CRM
  // members, so tenant admins (no membership) never resolve from it.
  const calledByName =
    call.agent_name ||
    getUserName(call.attended_by) ||
    getUserName(call.assigned_to) ||
    call.assigned_to_name ||
    "—";

  const assignedToName =
    call.assigned_to_name ||
    getUserName(call.assigned_to) ||
    call.contact_agent ||
    "—";

  const recordingUrl =
    call.recording_url || call.recording || call.audio_url || call.record_url || null;

  const formatDuration = (val) => {
    if (!val) return "—";
    if (typeof val === "string" && val.includes(":")) return val;
    const secs = Number(val);
    if (isNaN(secs)) return String(val);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 380 }, p: 0 } }}>
      <Box sx={{ bgcolor: COLORS.indigo, p: 2.5, color: "#fff" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ width: 42, height: 42, bgcolor: avatarColor(contactName) + "cc",
              fontSize: "0.85rem", fontWeight: 700 }}>
              {initials(contactName)}
            </Avatar>
            <Box>
              <Typography fontWeight={700} sx={{ fontSize: "0.95rem" }}>{contactName}</Typography>
              {companyName && (
                <Typography sx={{ fontSize: "0.75rem", opacity: 0.85 }}>{companyName}</Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff", mt: -0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
          <OutcomePill outcome={call.outcome || call.status} />
          {call.direction && (
            <Chip label={call.direction} size="small"
              sx={{ bgcolor: "#ffffff22", color: "#fff", fontWeight: 600,
                fontSize: "0.68rem", height: 22, borderRadius: "10px" }} />
          )}
        </Box>
      </Box>

      <Box p={2.5}>
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.68rem", mb: 1, display: "block" }}>
          Call Details
        </Typography>

        <DetailRow icon={AccessTime} label="Date & Time"
          value={dt ? format(dt, "MMM d, yyyy · h:mm a") : "—"} />
        <DetailRow icon={Phone} label="Duration"
          value={formatDuration(call.duration)} />
        <DetailRow icon={Person} label="Called By"
          value={calledByName} color={COLORS.green} />
        <DetailRow icon={Assignment} label="Assigned To"
          value={assignedToName} color={COLORS.indigo} />
        <DetailRow icon={Business} label="Account" value={companyName || "—"} />
        
        {call.phone_number && (
          <DetailRow icon={Phone} label="Phone Number" value={call.phone_number} />
        )}
        {call.notes && (
          <Box py={1} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Notes</Typography>
            <Typography variant="body2" sx={{ fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
              {call.notes}
            </Typography>
          </Box>
        )}

        {recordingUrl && (
          <Box mt={2}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.68rem",
                mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
              <RecordVoiceOver sx={{ fontSize: 14 }} /> Call Recording
            </Typography>
            <Box sx={{ bgcolor: "#f5f5f5", borderRadius: "10px", p: 1.5 }}>
              <audio controls style={{ width: "100%", height: 36 }}>
                <source src={recordingUrl} />
                Your browser does not support audio playback.
              </audio>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// ── Call Row ──────────────────────────────────────────────────────────────────
function CallRow({ call, onClick }) {
  const contactName = call.contact_name || "Unknown";
  const companyName = call.company_name || "";
  const dt = call.start_time ? parseISO(call.start_time) : null;
  const duration = call.duration || "—";
  const isAnswered = (call.outcome || call.status || "").toLowerCase() === "answered";

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      py={1.25}
      onClick={() => onClick(call)}
      sx={{
        cursor: "pointer",
        borderRadius: "8px",
        px: 0.5,
        "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" },
        "&:hover": { bgcolor: "#f5f7ff" },
        transition: "background 0.15s",
      }}
    >
      <Avatar sx={{ width: 34, height: 34, fontSize: "0.72rem", fontWeight: 700,
        bgcolor: avatarColor(contactName), flexShrink: 0 }}>
        {initials(contactName)}
      </Avatar>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px",
        bgcolor: isAnswered ? "#e8f5e9" : "#fce4ec",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {isAnswered ? (
          <PhoneCallback sx={{ fontSize: 16, color: COLORS.green }} />
        ) : (
          <PhoneMissed sx={{ fontSize: 16, color: COLORS.red }} />
        )}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: "0.8rem" }}>
          {companyName || contactName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dt ? format(dt, "MMM d, h:mm a") : "—"} · {duration}
        </Typography>
      </Box>
      <Box flexShrink={0}>
        <OutcomePill outcome={call.outcome || call.status} />
      </Box>
    </Box>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

export default function CallLogsPanel({
  calls = [],
  totalCount,
  isLoading = false,
  outcome,
  users = [],
  isActive = false,
}) {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState(null);

  const filtered = useMemo(() => {
    let result = calls;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          (c.contact_name || "").toLowerCase().includes(q) ||
          (c.company_name || "").toLowerCase().includes(q) ||
          (c.phone_number || "").includes(q)
      );
    }
    return result;
  }, [calls, search]);

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = page * PAGE_SIZE < filtered.length;

  return (
    <>
      <Card sx={{ 
        borderRadius: "16px", 
        boxShadow: isActive ? `0 0 0 2px ${COLORS.amber}, 0 6px 20px rgba(0,0,0,0.12)` : "0 2px 12px rgba(0,0,0,0.07)",
        transform: isActive ? "translateY(-4px)" : "none",
        transition: "all 0.3s ease",
        height: "100%" 
      }}>
        <CardContent sx={{ p: 2.5, pb: "12px !important" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
              Call Logs
              {!isLoading && (totalCount ?? filtered.length) > 0 && (
                <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                  ({(totalCount ?? filtered.length).toLocaleString()})
                </Typography>
              )}
            </Typography>
            <Typography
              variant="caption"
              component="span"
              onClick={() => history.push("/admin/crm/calls")}
              sx={{ cursor: "pointer", color: COLORS.indigo, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 0.3 }}
            >
              View all <ArrowForward sx={{ fontSize: 12 }} />
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search call logs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.82rem" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} display="flex" gap={1.5} mb={1.5}>
                <Skeleton variant="circular" width={34} height={34} />
                <Box flex={1}>
                  <Skeleton variant="text" width="65%" />
                  <Skeleton variant="text" width="45%" />
                </Box>
              </Box>
            ))
          ) : filtered.length === 0 ? (
            <Box py={4} textAlign="center">
              <Phone sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {search ? "No calls match your search" : "No call logs for this period"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
              {paginated.map((call) => (
                <CallRow key={call.id} call={call} onClick={setSelectedCall} />
              ))}
              {hasMore && (
                <Box textAlign="center" pt={1}>
                  <Typography
                    variant="caption"
                    onClick={() => setPage((p) => p + 1)}
                    sx={{ cursor: "pointer", color: COLORS.indigo, fontWeight: 600,
                      "&:hover": { textDecoration: "underline" } }}
                  >
                    Load more ({filtered.length - page * PAGE_SIZE} remaining)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <CallDetailDrawer
        call={selectedCall}
        open={Boolean(selectedCall)}
        onClose={() => setSelectedCall(null)}
        users={users}
      />
    </>
  );
}
